<template>
  <b-sidebar
    id="edit-hs-code-sidebar"
    :visible="!!selectedHSCodeId"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => !val && $emit('updated')"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Edit HS Code</h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <b-overlay :show="isLoading" spinner-variant="primary">
        <div class="p-2">
          <HSCodeForm
            :form-data.sync="hsCodeData"
            :loading="isSubmitting"
            @submit="onSubmit"
            v-if="!isLoading"
          />
        </div>
      </b-overlay>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BFormInvalidFeedback,
  BButton,
  BOverlay,
  BSpinner,
} from 'bootstrap-vue';
import { ref, computed, watch } from '@vue/composition-api';
import { required } from '@validations';
import formValidation from '@core/comp-functions/forms/form-validation';
import Ripple from 'vue-ripple-directive';
import store from '@/store';
import HSCodeForm from './HSCodeForm.vue';

export default {
  name: 'HSCodeListEdit',
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BFormInvalidFeedback,
    BButton,
    BOverlay,
    BSpinner,

    // Custom Components
    HSCodeForm,
  },
  directives: {
    Ripple,
  },
  props: {
    selectedHSCodeId: {
      type: [Number],
      default: null,
    },
  },
  data() {
    return {
      required,
    };
  },
  setup(props, { emit }) {
    const businessID = computed(() => store.state.user.businessID);
    const isLoading = ref(true);
    const isSubmitting = ref(false);

    const blankHSCodeData = {
      id: 0,
      code: '',
      name: '',
      description: '',
      isActive: true,
      businessID: businessID.value,
    };

    const hsCodeData = ref(JSON.parse(JSON.stringify(blankHSCodeData)));

    const resetHSCodeData = () => {
      hsCodeData.value = JSON.parse(
        JSON.stringify({
          ...blankHSCodeData,
          businessID: businessID.value,
        })
      );
    };

    const fetchHSCodeData = async () => {
      if (!props.selectedHSCodeId) return;

      isLoading.value = true;
      store
        .dispatch('fbr-invoice-hs-code/fetchHSCode', {
          id: props.selectedHSCodeId,
        })
        .then((hsCodeResponse) => {
          console.log('=== HSCodeListEdit fetchHSCodeData response:', hsCodeResponse);

          hsCodeData.value = {
            id: hsCodeResponse.id,
            code: hsCodeResponse.code,
            name: hsCodeResponse.name,
            description: hsCodeResponse.description,
            isActive: hsCodeResponse.isActive,
            businessID: businessID.value,
          };
        })
        .catch((error) => {
          console.error('Error fetching HS Code:', error);
          // Reset to blank data on error
          resetHSCodeData();
        })
        .finally(() => {
          isLoading.value = false;
        });
    };

    const onSubmit = async () => {
      console.log('onSubmit');

      isSubmitting.value = true;

      // Prepare data for submission - include the id and userID
      const submissionData = {
        id: props.selectedHSCodeId,
        code: hsCodeData.value.code,
        name: hsCodeData.value.name,
        description: hsCodeData.value.description,
        isActive: hsCodeData.value.isActive,
        businessID: businessID.value,
        userID: store.state.user.userID,
      };

      try {
        await store.dispatch('fbr-invoice-hs-code/editHSCode', submissionData);
        emit('refetch-data');
        emit('updated'); // This will hide the form as parent sets selectedHSCodeId to null
      } catch (error) {
        console.error('Error updating HS Code:', error);
        // Handle error (you can add toast notification here if needed)
      } finally {
        isSubmitting.value = false;
      }
    };

    // Watch for selectedHSCodeId changes to fetch data
    watch(
      () => props.selectedHSCodeId,
      (newId) => {
        if (newId) {
          fetchHSCodeData();
        } else {
          resetHSCodeData();
        }
      },
      { immediate: true }
    );

    watch(
      hsCodeData,
      (newData) => {
        console.log('=== HSCodeListEdit hsCodeData changed:', newData);
      },
      { deep: true }
    );

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetHSCodeData);

    return {
      hsCodeData,
      isLoading,
      isSubmitting,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
    };
  },
};
</script>

<style lang="scss" scoped>
#edit-hs-code-sidebar {
  .content-sidebar-header {
    padding: 1rem 1.5rem !important;
  }
}
</style>
